import React, { useState, useEffect, useRef, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown } from "lucide-react";

interface DropdownItem {
  label: string;
  href?: string;
  onClick?: () => void;
  icon?: ReactNode;
  disabled?: boolean;
}

interface DropdownProps {
  trigger: ReactNode;
  items: DropdownItem[];
  className?: string;
  dropdownClassName?: string;
  position?: "left" | "right" | "center";
  hoverOnDesktop?: boolean;
}

const Dropdown: React.FC<DropdownProps> = ({
  trigger,
  items,
  className = "",
  dropdownClassName = "",
  position = "left",
  hoverOnDesktop = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const handleMouseEnter = () => {
    if (hoverOnDesktop && !isMobile) {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      setIsOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (hoverOnDesktop && !isMobile) {
      hoverTimeoutRef.current = setTimeout(() => {
        setIsOpen(false);
      }, 150); // Small delay to prevent flickering
    }
  };

  const handleClick = () => {
    if (!hoverOnDesktop || isMobile) {
      setIsOpen(!isOpen);
    }
  };

  const handleItemClick = (item: DropdownItem) => {
    if (item.disabled) return;

    if (item.onClick) {
      item.onClick();
    } else if (item.href) {
      navigate(item.href);
    }

    setIsOpen(false);
  };

  const getPositionClass = () => {
    switch (position) {
      case "right":
        return "right-0";
      case "center":
        return "left-1/2 transform -translate-x-1/2";
      default:
        return "left-0";
    }
  };

  return (
    <div
      className={`relative ${className}`}
      ref={dropdownRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Trigger */}
      <div onClick={handleClick} className="cursor-pointer">
        {trigger}
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={`
          absolute top-full mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50
          ${getPositionClass()}
          ${dropdownClassName}
        `}
        >
          {items.map((item, index) => (
            <div key={index}>
              <button
                onClick={() => handleItemClick(item)}
                disabled={item.disabled}
                className={`
                  w-full flex items-center space-x-3 px-4 py-2 text-sm text-left transition-colors duration-200
                  ${
                    item.disabled
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-deep-umber hover:bg-tabby-light hover:text-main-purple"
                  }
                `}
              >
                {item.icon && (
                  <div className="flex-shrink-0 w-4 h-4">{item.icon}</div>
                )}
                <span className="flex-1">{item.label}</span>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
